import React from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { sound } from '../../utils/sound.js';

export type ButtonVariant = 'primary' | 'gold' | 'cyan' | 'purple' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface AnimatedButtonProps extends Omit<HTMLMotionProps<'button'>, 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  triggerHaptic?: () => void;
}

export const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  children,
  className = '',
  disabled,
  onClick,
  triggerHaptic,
  ...motionProps
}) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) return;
    sound.playTap();
    if (triggerHaptic) triggerHaptic();
    if (onClick) onClick(e);
  };

  const sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-3 py-1.5 text-xs rounded-xl space-x-1.5',
    md: 'px-5 py-2.5 text-xs sm:text-sm rounded-2xl space-x-2',
    lg: 'px-6 py-3.5 text-sm sm:text-base rounded-2xl space-x-2.5 font-black',
  };

  const variantClasses: Record<ButtonVariant, string> = {
    primary:
      'bg-gradient-to-r from-indigo-500 via-purple-600 to-pink-500 text-white shadow-lg shadow-purple-500/25 hover:brightness-110 active:brightness-95 border border-purple-400/30',
    gold:
      'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-midnight-950 font-black shadow-lg shadow-amber-500/30 hover:brightness-110 active:brightness-95 border border-yellow-200/50',
    cyan:
      'bg-gradient-to-r from-cyan-400 via-teal-400 to-blue-500 text-midnight-950 font-black shadow-lg shadow-cyan-500/30 hover:brightness-110 active:brightness-95 border border-cyan-200/50',
    purple:
      'bg-gradient-to-r from-purple-600 via-indigo-600 to-violet-700 text-white shadow-lg shadow-indigo-600/25 hover:brightness-110 active:brightness-95 border border-purple-400/30',
    danger:
      'bg-gradient-to-r from-rose-500 to-red-600 text-white shadow-lg shadow-rose-500/25 hover:brightness-110 border border-rose-400/30',
    ghost:
      'bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10 active:bg-white/15',
  };

  const disabledClasses = disabled || isLoading ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer';

  return (
    <motion.button
      whileHover={disabled || isLoading ? undefined : { scale: 1.02 }}
      whileTap={disabled || isLoading ? undefined : { scale: 0.97 }}
      transition={{ duration: 0.15, ease: 'easeOut' }}
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={`relative inline-flex items-center justify-center font-bold tracking-wide transition-all overflow-hidden select-none ${sizeClasses[size]} ${variantClasses[variant]} ${disabledClasses} ${className}`}
      {...motionProps}
    >
      {/* Subtle Specular Sheen Effect */}
      <span className="absolute inset-0 w-full h-full bg-gradient-to-t from-transparent via-white/10 to-transparent pointer-events-none" />

      {isLoading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin text-current" />
          <span>Processing...</span>
        </>
      ) : (
        <>
          {icon && <span className="flex-shrink-0">{icon}</span>}
          <span>{children}</span>
        </>
      )}
    </motion.button>
  );
};
